import { useEffect, useState } from 'react';
import type { Tv2mSdk } from '@apm/tv2m-client-sdk';
import type { BloomRuntimeConfig } from '@apm/contracts';
import type { Tv2mState } from '../useTv2m.js';
import { config } from '../config.js';
import { describeSwReportDiag, readSwReportDiag, type SwReportDiag } from '../sw-diagnostics.js';

/**
 * `?debug=1` 진단 화면.
 *
 * DoD 가 요구하는 넷을 보여 준다 — `deviceId` / `householdId` / 구독 endpoint /
 * 최근 이벤트. **이 넷이 "알림이 안 온다" 를 가르는 질문 그대로다**: 등록됐는가,
 * 어느 가구인가, 발송처가 있는가, 이벤트가 서버에 닿는가.
 *
 * 500ms 주기로 폴링해 그린다 (리시버의 `DebugPanel` 과 같은 방침). SDK 의 상태를
 * React state 로 끌어올리면 계측이 앱의 렌더 경로를 바꾸고, 그러면 `?debug=1` 여부가
 * 동작을 바꾼다.
 */
export function DebugPage({
  state,
  sdk,
  runtime,
}: {
  state: Tv2mState;
  sdk: Tv2mSdk | null;
  /** `GET /v1/bloom/config` 가 준 설정. **모드가 여기 드러나야 한다** — 구독이 어느
   *  서버에 붙었는지가 그 값으로 정해지고, 틀리면 알림만 조용히 오지 않는다 */
  runtime: BloomRuntimeConfig;
}) {
  const snapshot = usePolledSnapshot(sdk);
  const [endpoint, setEndpoint] = useState<string | null>(null);
  const worker = useServiceWorker();
  const swDiag = usePolledSwDiag();

  // 구독 endpoint 는 브라우저에게 물어야 안다 — 서버에 무엇이 등록됐는지가 아니라
  // **이 기기가 지금 무엇을 들고 있는지**가 알고 싶은 값이다.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    void navigator.serviceWorker.ready
      .then((registration) => registration.pushManager.getSubscription())
      .then((subscription) => {
        setEndpoint(subscription?.endpoint ?? null);
      })
      .catch(() => {
        setEndpoint(null);
      });
  }, [state.init?.pushSubscribed]);

  const init = state.init;

  return (
    <>
      <div className="card">
        <h2 className="card__title">진단</h2>
        <dl className="kv">
          <dt>deviceId</dt>
          <dd>{init?.deviceId ?? '-'}</dd>
          <dt>householdId</dt>
          <dd>{init?.householdId ?? '(등록 실패)'}</dd>
          <dt>firstRun</dt>
          <dd>{String(init?.firstRun ?? false)}</dd>
          <dt>push 권한</dt>
          <dd>{init?.pushPermission ?? '-'}</dd>
          <dt>서버 구독</dt>
          <dd>{String(init?.pushSubscribed ?? false)}</dd>
          <dt>iOS 설치 필요</dt>
          <dd>{String(state.needsIosInstall)}</dd>
          {/*
           * ── 셋이 「알림이 안 온다」를 가른다 (v12) ──────────────
           *
           * **셸 안에서 볼 수 있는 진단은 이 화면뿐이다.** `?debug=1` 은 모듈 스코프에서
           * 쿼리를 읽어 정하는데 셸에는 주소창이 없어 `WatchScreen` 의 패널에 닿을 수
           * 없고, 갈 수 있는 것은 설정 시트의 `/debug` 링크 하나다.
           *
           * 2026-08-12 데모에서 이 셋이 없어 원인을 못 갈랐다 — 「기동이 안 끝났다」와
           * 「셸이 답하지 않았다」와 「서버에 요청이 닿지 않았다」가 화면에서 전부
           * 「알림을 켜 주세요」로 보였다.
           */}
          <dt>기동 완료</dt>
          <dd>{String(state.ready)}</dd>
          <dt>네이티브 등록</dt>
          <dd>{describeNativePush(state.nativePush)}</dd>
          <dt>BLOOM 등록</dt>
          <dd>{describeRegister(state)}</dd>
          <dt>heartbeat(초)</dt>
          <dd>{init?.nextHeartbeatInSeconds ?? '-'}</dd>
          <dt>route</dt>
          <dd>{state.route.kind}</dd>
          <dt>context</dt>
          <dd>{state.context === null ? '-' : JSON.stringify(state.context)}</dd>
          <dt>deferred</dt>
          <dd>{state.deferred === null ? '(조회 안 함)' : JSON.stringify(state.deferred)}</dd>
          <dt>구독 endpoint</dt>
          <dd>{endpoint ?? '(없음)'}</dd>
          {/*
           * ── SW 가 설정을 들고 있는가 (2026-08-12) ────────────────
           *
           * `public/sw.js` 는 앱이 닫힌 채 깨어나 서버 주소·API Key·deviceId 를
           * **등록 URL 의 쿼리에서만** 읽는다 (`sw-registration.ts`). 그것이 비면
           * `report` 가 **조용히 건너뛰고**, 그러면 알림은 뜨는데 `push_received` 가
           * 서버에 남지 않는다 — `docs/18` §1 의 판정표가 그것을 「버려졌다」로
           * 오진한다.
           *
           * 실기에서 정확히 그 조합을 봤다(알림은 오는데 보고가 없다). **이 줄이 그
           * 추측을 관측으로 바꾼다** — 쿼리가 보이면 SW 는 알고 있는 것이고, 원인은
           * 그 뒤(요청 자체)에 있다.
           */}
          <dt>SW 스크립트</dt>
          <dd>{describeWorker(worker)}</dd>
          {/*
           * **`sent` 인데 서버에 기록이 없으면 그것은 서버 쪽 이야기다.** 그 구분이
           * 이 줄의 존재 이유이고, 없으면 「알림은 오는데 보고가 없다」를 설명할
           * 방법이 아예 없다 (`sw-diagnostics.ts`).
           */}
          <dt>SW 보고</dt>
          <dd>{describeSwReportDiag(swDiag)}</dd>
          <dt>이벤트 큐</dt>
          <dd>
            대기 {snapshot.pending} · 버림 {snapshot.dropped}
          </dd>
          <dt>통합 모드</dt>
          <dd>{runtime.mode}</dd>
          <dt>로그인 필요</dt>
          <dd>{String(runtime.requireLogin)}</dd>
          <dt>VAPID 공개키</dt>
          <dd>{runtime.vapidPublicKey === '' ? '(없음)' : runtime.vapidPublicKey}</dd>
          <dt>TV2M 서버</dt>
          <dd>{runtime.tv2mServerUrl}</dd>
          <dt>BLOOM Server</dt>
          <dd>{config.serverUrl}</dd>
        </dl>
      </div>

      <div className="card">
        <h2 className="card__title">최근 로그 10건</h2>
        {snapshot.log.length === 0 ? (
          <p className="card__text">아직 없다.</p>
        ) : (
          <ul className="log">
            {snapshot.log.map((entry) => (
              <li key={`${entry.at}-${entry.message}`}>
                {entry.at.slice(11, 23)} {entry.message}
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

/**
 * SW 의 마지막 보고를 주기적으로 읽는다.
 *
 * **폴링인 이유**: 알림은 이 화면을 열어 둔 채로도 온다. 한 번만 읽으면 「방금 온
 * 알림이 보고됐는가」를 보려고 화면을 다시 열어야 한다.
 */
function usePolledSwDiag(): SwReportDiag | null {
  const [diag, setDiag] = useState<SwReportDiag | null>(null);
  useEffect(() => {
    const read = () => {
      void readSwReportDiag().then(setDiag);
    };
    read();
    const timer = window.setInterval(read, 1000);
    return () => {
      window.clearInterval(timer);
    };
  }, []);
  return diag;
}

interface WorkerInfo {
  /** 등록된 스크립트 URL. 없으면 `null` */
  scriptUrl: string | null;
  state: string | null;
}

/**
 * 등록된 SW 를 읽는다. **`ready` 를 쓰지 않는다** — 등록이 없으면 영원히 pending 이라
 * 진단 화면이 그것 때문에 멈춘다 (오늘 부팅에서 고친 것과 같은 종류다).
 */
function useServiceWorker(): WorkerInfo | null {
  const [info, setInfo] = useState<WorkerInfo | null>(null);
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    void navigator.serviceWorker
      .getRegistration()
      .then((registration) => {
        const worker = registration?.active ?? registration?.installing ?? registration?.waiting;
        setInfo({ scriptUrl: worker?.scriptURL ?? null, state: worker?.state ?? null });
      })
      .catch(() => {
        setInfo({ scriptUrl: null, state: null });
      });
  }, []);
  return info;
}

/**
 * SW 가 설정을 들고 있는가.
 *
 * **API Key 는 가린다.** 진단 화면이지만 그 값으로 남의 이벤트를 넣을 수 있고, 여기서
 * 알고 싶은 것은 「있는가」이지 값이 아니다.
 */
function describeWorker(info: WorkerInfo | null): string {
  if (info === null) return '(읽는 중)';
  if (info.scriptUrl === null) return '없음 — 아직 구독하지 않았다';
  const query = info.scriptUrl.includes('?')
    ? info.scriptUrl.slice(info.scriptUrl.indexOf('?'))
    : '';
  const params = new URLSearchParams(query);
  const server = params.get('server');
  const device = params.get('device');
  const key = params.get('key');
  if (server === null || device === null) {
    // **이 상태가 조용한 실패의 원인이다** — SW 가 서버 주소를 몰라 보고를 건너뛴다.
    return `${info.state ?? '?'} · 설정 없음 (옛 등록) — 보고가 건너뛰어진다`;
  }
  return `${info.state ?? '?'} · ${server} · ${device.slice(0, 8)} · key ${key === null || key === '' ? '없음' : '있음'}`;
}

/** 셸에게 APNs 등록을 물은 결과. 웹·PWA 경로면 브리지가 없다 */
function describeNativePush(kind: Tv2mState['nativePush']): string {
  switch (kind) {
    case 'ready':
      return '있음';
    case 'missing':
      return '없음 (권한 전이거나 토큰 미도착)';
    case 'timeout':
      // **이 값이 보이면 셸이 상한 안에 답하지 않은 것이다.** 늦게라도 오면 그때
      // 다시 등록하므로 아래 「BLOOM 등록」이 뒤이어 성공으로 바뀔 수 있다.
      return '시간 초과 — 셸이 답하지 않았다';
    default:
      return '(셸 아님)';
  }
}

/**
 * BLOOM Server 기기 등록의 결말.
 *
 * **`pushReady` 가 곧 「이 폰이 발송 대상인가」다** — 서버가 행의 수단을 보고 판정한다
 * (`hasPushMeans`). 여기가 `false` 면 알림이 오지 않고, 그 이유는 옆의 두 줄에 있다.
 */
function describeRegister(state: Tv2mState): string {
  if (state.registerError !== null) return `실패 — ${state.registerError}`;
  if (state.device === null) return '(안 함)';
  return `ok · 발송 가능 ${String(state.device.pushReady)} · 계정 ${String(
    state.device.linkedToAccount,
  )}${state.device.reason === undefined ? '' : ` · ${state.device.reason}`}`;
}

interface Snapshot {
  pending: number;
  dropped: number;
  log: ReadonlyArray<{ message: string; at: string }>;
}

const EMPTY: Snapshot = { pending: 0, dropped: 0, log: [] };

function usePolledSnapshot(sdk: Tv2mSdk | null): Snapshot {
  const [snapshot, setSnapshot] = useState<Snapshot>(EMPTY);

  useEffect(() => {
    if (sdk === null) return;
    const read = () => {
      const stats = sdk.getEventStats();
      setSnapshot({
        pending: stats.pending,
        dropped: stats.dropped,
        // 최근 것이 위로 온다. 밑으로 쌓으면 화면을 스크롤해야 방금 일어난 일이 보인다.
        log: sdk.getDebugLog().slice(-10).reverse(),
      });
    };
    read();
    const timer = window.setInterval(read, 500);
    return () => {
      window.clearInterval(timer);
    };
  }, [sdk]);

  return snapshot;
}
